import { MsalProvider, UnauthenticatedTemplate, useMsal } from '@azure/msal-react';
import { Field } from '@lucidtech/las-sdk-browser';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import InfoIcon from '@/assets/info-icon.svg?react';
import {
  Button,
  Flex,
  Heading,
  Label,
  Loading,
  Radio,
  RadioGroup,
  Switch,
  Text,
  TruncatedText,
} from '@/components/core';
import { ConnectToVideo, HowToGuide } from '@/components/flow';
import { MicrosoftLogo } from '@/components/logos';
import { AUTH_SCOPES, EXTENDED_AUTH_SCOPES, GraphClient, ItemType, msalInstance } from '@/connectors/msgraph';
import { useGetAgentFromPath } from '@/hooks';
import {
  toConnectionId,
  toModelId,
  useDeleteConnection,
  useGetConnection,
  useGetModel,
  useUpdateConnection,
} from '@/hooks/api';
import { useTriggerStore } from '@/store';
import { findModelId } from '@/utils';

import { FieldMapper } from './FieldMapper';
import { FileSelector } from './FileSelector';
import { TableSelector } from './TableSelector';

const createFieldToIndexMap = (fields: Record<string, Field>) => {
  let idx = 0;

  const result: Record<string, number> = {};
  for (const fieldId of Object.keys(fields).sort()) {
    if (fields[fieldId].type !== 'table') {
      result[fieldId] = idx;
      idx += 1;
    } else {
      const subfields = fields[fieldId].fields as Record<string, Field>;
      for (const subfieldId of Object.keys(subfields).sort()) {
        result[subfieldId] = idx;
        idx += 1;
      }
    }
  }

  return result;
};

export type ExportExcelConfigurationProps = object;

// eslint-disable-next-line no-empty-pattern
const IExportExcelConfiguration = ({}: ExportExcelConfigurationProps) => {
  const action = useTriggerStore((state) => state.action);
  const updateAction = useTriggerStore((state) => state.updateAction);
  const { data: agent } = useGetAgentFromPath();
  const { data: model } = useGetModel(toModelId(findModelId(agent)));
  const { instance } = useMsal();

  const { data: connection, isFetching } = useGetConnection(toConnectionId(action?.connectionId));
  const { mutateAsync: updateConnection } = useUpdateConnection();
  const { mutateAsync: deleteConnection } = useDeleteConnection();

  const hasRequestedElevatedPermissions = connection?.metadata?.hasRequestedElevatedPermissions;

  const [loggedInMicrosoftUser, setLoggedInMicrosoftUser] = useState(null);

  useEffect(() => {
    const accessToken = connection?.credentials?.accessToken as string | undefined;
    if (!accessToken) return;

    const client = new GraphClient(accessToken);
    client.getLoggedInUser().then((user) => {
      if (user) {
        setLoggedInMicrosoftUser(user['mail']);
      }
    });
  }, [connection?.credentials?.accessToken]);

  const hasElevatedScopes = useMemo(() => {
    const accessToken = connection?.credentials?.accessToken as string | undefined;
    return new GraphClient(accessToken).checkHasElevatedScopes();
  }, [connection?.credentials?.accessToken]);

  const handleLogin = useCallback(async () => {
    if (action.actionId && agent?.agentId) {
      instance
        ?.loginRedirect({
          scopes: AUTH_SCOPES,
          state: JSON.stringify({
            actionId: action.actionId,
            agentId: agent.agentId,
            scopesRequested: AUTH_SCOPES,
          }),
        })
        .catch(console.error);
    }
  }, [action.actionId, agent, instance]);

  const handleLogout = useCallback(() => {
    instance
      ?.logoutPopup()
      ?.catch(console.error)
      ?.then(() => {
        deleteConnection(toConnectionId(action?.connectionId));
        updateAction({
          connectionId: null,
          config: {},
        });
      });
  }, [action, deleteConnection, instance, updateAction]);

  const requestElevatedPermissions = useCallback(async () => {
    if (action.actionId && agent?.agentId) {
      console.log('Updating connection:');
      const response = await updateConnection({
        id: toConnectionId(connection?.connectionId),
        updates: {
          metadata: {
            hasRequestedElevatedPermissions: true,
          },
        },
      });

      console.log('Response:', response);

      instance
        ?.loginRedirect({
          scopes: EXTENDED_AUTH_SCOPES,
          state: JSON.stringify({
            actionId: action.actionId,
            agentId: agent.agentId,
            scopesRequested: EXTENDED_AUTH_SCOPES,
          }),
        })
        .catch(console.error);
    }
  }, [action.actionId, agent.agentId, connection?.connectionId, instance, updateConnection]);

  const setSaveOriginalDocument = useCallback(
    (checked: boolean) => {
      updateAction((state) => ({ config: { ...state.config, saveOriginalDocument: checked } }));
    },
    [updateAction]
  );

  const setCreateNewWorkbook = useCallback(
    (val: string) => {
      const checked = val === 'separate' ? true : false;
      const fieldToIndexMap = createFieldToIndexMap(model?.fieldConfig || {});
      updateAction((state) => ({
        config: {
          ...state.config,
          createNewWorkbook: checked,
          fieldToIndexMap: checked ? fieldToIndexMap : {},
          driveId: null,
          driveItemId: null,
          siteId: null,
        },
      }));
    },
    [model?.fieldConfig, updateAction]
  );

  const createNewWorkbook = Boolean(action?.config?.createNewWorkbook);

  return (
    <div className="flex grow flex-col gap-2 p-4">
      {!agent || !action || isFetching ? (
        <Loading label="Loading" />
      ) : (
        connection && (
          <Flex col variant="plain" className="gap-md">
            <div className="gap-sm mr-1 flex flex-col">
              <div className="gap-sm flex items-center">
                <Text size="xs" className="font-semibold text-gray-600">
                  Account
                </Text>

                <div className="grow" />

                <div className="border-success-100 bg-success-500 size-3 rounded-full border-3" />
                <Text size="xs" className="text-success-700 font-semibold">
                  Connected
                </Text>
              </div>

              <Flex className="p-sm gap-md items-center rounded-md border-1 border-gray-200 bg-gray-50">
                <MicrosoftLogo className="shrink-0" size="xl" />

                <TruncatedText className="grow-0">{loggedInMicrosoftUser}</TruncatedText>
                <div className="grow" />

                <Button className="shrink-0" variant="secondary" onPress={handleLogout}>
                  <Text>Disconnect</Text>
                </Button>
              </Flex>
            </div>

            {hasRequestedElevatedPermissions ? (
              <Flex col variant="warning" className="gap-sm p-sm mr-1 items-start rounded-sm">
                <Flex col>
                  <Text className="font-bold">Waiting for admin approval</Text>
                  <Text>Click Reconnect once it’s approved.</Text>
                </Flex>

                <Flex className="gap-xs" variant="plain">
                  <Button variant="tertiary" onPress={requestElevatedPermissions}>
                    <Flex className="gap-xs">
                      <Text size="xs">Reconnect</Text>
                    </Flex>
                  </Button>
                </Flex>
              </Flex>
            ) : hasRequestedElevatedPermissions ? (
              <Flex col variant="warning" className="gap-sm p-sm mr-1 items-start rounded-sm">
                <Text>
                  <strong>Some files may not be visible. </strong>
                  Files stored in shared Sites, SharePoint libraries, or other shared locations may require additional
                  permissions.
                </Text>
                <Flex className="gap-xs" variant="plain">
                  <Button variant="tertiary" onPress={requestElevatedPermissions}>
                    <Flex className="gap-xs">
                      <Text size="xs">Reconnect</Text>
                    </Flex>
                  </Button>
                </Flex>
              </Flex>
            ) : null}

            <Flex col className="gap-md">
              <RadioGroup onChange={setCreateNewWorkbook} value={createNewWorkbook ? 'separate' : 'single'}>
                <Radio value="separate">
                  <Text slot="description">Export to separate workbooks</Text>
                </Radio>
                <Radio value="single">
                  <Text slot="description">Export to single workbook</Text>
                </Radio>
              </RadioGroup>

              <FileSelector
                connection={connection}
                label={createNewWorkbook ? 'Folder' : 'Workbook'}
                description={createNewWorkbook ? 'Choose a folder where the workbooks will be saved.' : undefined}
                itemType={createNewWorkbook ? ItemType.Folder : ItemType.Workbook}
                requestElevatedPermission={requestElevatedPermissions}
              />

              <Switch onChange={setSaveOriginalDocument} isSelected={Boolean(action?.config?.saveOriginalDocument)}>
                <Label className="text-sm">Save the original document in the same folder as the workbook.</Label>
              </Switch>

              {!createNewWorkbook ? (
                <>
                  <TableSelector connection={connection} />
                  <FieldMapper connection={connection} />
                </>
              ) : null}
            </Flex>
          </Flex>
        )
      )}

      {!connection && !isFetching && (
        <UnauthenticatedTemplate>
          <div className="gap-sm flex items-center">
            <Text size="xs" className="font-semibold text-gray-600">
              Account
            </Text>

            <div className="grow" />

            <div className="size-3 rounded-full border-3 border-gray-100 bg-gray-500" />
            <Text size="xs" className="font-semibold text-gray-500">
              Not connected
            </Text>
          </div>

          <Flex className="p-sm gap-md items-center rounded-md border-1 border-gray-200 bg-gray-50">
            <MicrosoftLogo size="xl" />

            <Text className="grow">Sign in to connect</Text>

            <Button variant="primary" onPress={handleLogin}>
              <Text>Sign in</Text>
            </Button>
          </Flex>

          <Flex variant="primary" className="p-sm gap-sm content-start rounded-md border-1">
            <InfoIcon className="text-primary-500 mt-[0.1rem] size-[0.85rem]" />

            <Flex col className="border-0">
              <Heading size="xs">Use a work or school account</Heading>
              <Text size="xs">Personal Microsoft accounts are not currently supported.</Text>
            </Flex>
          </Flex>
        </UnauthenticatedTemplate>
      )}

      <div className="grow" />

      {action.actionId ? (
        <ConnectToVideo
          actionId={action.actionId}
          thumbnailUrl="https://public.cradl.ai/app/connecting-to-excel.png"
          url="https://www.youtube.com/watch?v=n9v9lWkD2lE"
          title="Connecting to Excel"
        />
      ) : null}

      <HowToGuide actionId={action.actionId} />
    </div>
  );
};

const IExportExcelConfigurationWrapper = ({ ...rest }: ExportExcelConfigurationProps) => {
  return (
    <MsalProvider instance={msalInstance}>
      <IExportExcelConfiguration {...rest} />
    </MsalProvider>
  );
};

export const ExportExcelConfiguration = memo(IExportExcelConfigurationWrapper) as typeof IExportExcelConfiguration;
